import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  UserProtocol,
  CreateProtocolRequest,
  UpdateProtocolRequest,
  ProtocolFilters,
} from './features/types/protocols';
import * as protocolApi from './features/utils/protocolApiUtils';

// Async thunks
export const fetchUserProtocols = createAsyncThunk(
  'protocols/fetchUserProtocols',
  async (filters: ProtocolFilters = {}) => {
    const response = await protocolApi.getUserProtocols(filters);
    return response;
  }
);

export const searchUserProtocols = createAsyncThunk(
  'protocols/searchUserProtocols',
  async ({ query, filters }: { query: string; filters?: Omit<ProtocolFilters, 'search'> }) => {
    const response = await protocolApi.searchProtocols(query, filters);
    return response;
  }
);

export const fetchProtocolById = createAsyncThunk(
  'protocols/fetchProtocolById',
  async (id: number) => {
    const response = await protocolApi.getProtocolById(id);
    return response;
  }
);

export const createNewProtocol = createAsyncThunk(
  'protocols/createNewProtocol',
  async (protocol: CreateProtocolRequest) => {
    const response = await protocolApi.createProtocol(protocol);
    return response;
  }
);

export const updateExistingProtocol = createAsyncThunk(
  'protocols/updateExistingProtocol',
  async ({ id, protocol }: { id: number; protocol: UpdateProtocolRequest }) => {
    const response = await protocolApi.updateProtocol(id, protocol);
    return response;
  }
);

export const deleteUserProtocol = createAsyncThunk(
  'protocols/deleteUserProtocol',
  async (id: number) => {
    await protocolApi.deleteProtocol(id);
    return id;
  }
);

// Admin async thunks
export const fetchAllProtocols = createAsyncThunk(
  'protocols/fetchAllProtocols',
  async (filters: ProtocolFilters = {}) => {
    const response = await protocolApi.getAllProtocols(filters);
    return response;
  }
);

export const updateProtocolAdmin = createAsyncThunk(
  'protocols/updateProtocolAdmin',
  async ({ id, protocol }: { id: number; protocol: UpdateProtocolRequest }) => {
    const response = await protocolApi.updateProtocolAdmin(id, protocol);
    return response;
  }
);

export const deleteProtocolAdmin = createAsyncThunk(
  'protocols/deleteProtocolAdmin',
  async (id: number) => {
    await protocolApi.deleteProtocolAdmin(id);
    return id;
  }
);

// State interface
interface ProtocolState {
  // User protocols
  userProtocols: UserProtocol[];
  totalUserProtocols: number;
  currentUserProtocol: UserProtocol | null;

  // Admin protocols
  allProtocols: UserProtocol[];
  totalAllProtocols: number;
  currentAdminProtocol: UserProtocol | null;

  // Loading states
  loading: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;

  // Error states
  error: string | null;

  // Filters and pagination
  filters: ProtocolFilters;
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const initialState: ProtocolState = (() => {
  const savedProtocols = localStorage.getItem('userProtocols');
  const savedTotal = localStorage.getItem('totalUserProtocols');

  return {
    userProtocols: savedProtocols ? JSON.parse(savedProtocols) : [],
    totalUserProtocols: savedTotal ? parseInt(savedTotal) : 0,
    currentUserProtocol: null,

    allProtocols: [],
    totalAllProtocols: 0,
    currentAdminProtocol: null,

    loading: false,
    creating: false,
    updating: false,
    deleting: false,

    error: null,

    filters: {
      specialty: undefined,
      status: 'all',
      search: undefined,
    },
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  };
})();

const protocolSlice = createSlice({
  name: 'protocols',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentProtocol: (state) => {
      state.currentUserProtocol = null;
      state.currentAdminProtocol = null;
    },
    setFilters: (state, action: PayloadAction<Partial<ProtocolFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setItemsPerPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
      state.currentPage = 1;
    },
    resetFilters: (state) => {
      state.filters = {
        specialty: undefined,
        status: 'all',
        search: undefined,
      };
      state.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    // Fetch user protocols
    builder
      .addCase(fetchUserProtocols.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProtocols.fulfilled, (state, action) => {
        state.loading = false;

        // Handle both array response and object response
        if (Array.isArray(action.payload)) {
          state.userProtocols = action.payload;
          state.totalUserProtocols = action.payload.length;
          state.totalPages = 1;
          state.hasNext = false;
          state.hasPrev = false;
        } else {
          state.userProtocols = action.payload.protocols || [];
          state.totalUserProtocols = action.payload.total || 0;
          state.totalPages = action.payload.total_pages || 1;
          state.hasNext = action.payload.has_next || false;
          state.hasPrev = action.payload.has_prev || false;
          state.currentPage = action.payload.page || 1;
        }

        // Сохраняем в localStorage
        localStorage.setItem('userProtocols', JSON.stringify(state.userProtocols));
        localStorage.setItem('totalUserProtocols', state.totalUserProtocols.toString());
      })
      .addCase(fetchUserProtocols.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch protocols';
      });

    // Search user protocols
    builder
      .addCase(searchUserProtocols.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchUserProtocols.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both array response and object response
        if (Array.isArray(action.payload)) {
          state.userProtocols = action.payload;
          state.totalUserProtocols = action.payload.length;
          state.totalPages = 1;
          state.hasNext = false;
          state.hasPrev = false;
        } else {
          state.userProtocols = action.payload.protocols || [];
          state.totalUserProtocols = action.payload.total || 0;
          state.totalPages = action.payload.total_pages || 1;
          state.hasNext = action.payload.has_next || false;
          state.hasPrev = action.payload.has_prev || false;
          state.currentPage = action.payload.page || 1;
        }
      })
      .addCase(searchUserProtocols.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to search protocols';
      });

    // Fetch protocol by ID
    builder
      .addCase(fetchProtocolById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProtocolById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUserProtocol = action.payload;
      })
      .addCase(fetchProtocolById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch protocol';
      });

    // Create new protocol
    builder
      .addCase(createNewProtocol.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createNewProtocol.fulfilled, (state, action) => {
        state.creating = false;
        state.userProtocols.unshift(action.payload);
        state.totalUserProtocols += 1;

        // Сохраняем в localStorage
        localStorage.setItem('userProtocols', JSON.stringify(state.userProtocols));
        localStorage.setItem('totalUserProtocols', state.totalUserProtocols.toString());
      })
      .addCase(createNewProtocol.rejected, (state, action) => {
        state.creating = false;
        state.error = action.error.message || 'Failed to create protocol';
      });

    // Update existing protocol
    builder
      .addCase(updateExistingProtocol.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateExistingProtocol.fulfilled, (state, action) => {
        state.updating = false;
        const index = state.userProtocols.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.userProtocols[index] = action.payload;
        }
        if (state.currentUserProtocol?.id === action.payload.id) {
          state.currentUserProtocol = action.payload;
        }

        // Сохраняем в localStorage
        localStorage.setItem('userProtocols', JSON.stringify(state.userProtocols));
      })
      .addCase(updateExistingProtocol.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || 'Failed to update protocol';
      });

    // Delete user protocol
    builder
      .addCase(deleteUserProtocol.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteUserProtocol.fulfilled, (state, action) => {
        state.deleting = false;
        state.userProtocols = state.userProtocols.filter(p => p.id !== action.payload);
        state.totalUserProtocols -= 1;
        if (state.currentUserProtocol?.id === action.payload) {
          state.currentUserProtocol = null;
        }
      })
      .addCase(deleteUserProtocol.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.error.message || 'Failed to delete protocol';
      });

    // Admin: Fetch all protocols
    builder
      .addCase(fetchAllProtocols.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllProtocols.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both array response and object response
        if (Array.isArray(action.payload)) {
          state.allProtocols = action.payload;
          state.totalAllProtocols = action.payload.length;
        } else {
          state.allProtocols = action.payload.protocols || [];
          state.totalAllProtocols = action.payload.total || 0;
        }
      })
      .addCase(fetchAllProtocols.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch all protocols';
      });

    // Admin: Update protocol
    builder
      .addCase(updateProtocolAdmin.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateProtocolAdmin.fulfilled, (state, action) => {
        state.updating = false;
        const index = state.allProtocols.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.allProtocols[index] = action.payload;
        }
        if (state.currentAdminProtocol?.id === action.payload.id) {
          state.currentAdminProtocol = action.payload;
        }
      })
      .addCase(updateProtocolAdmin.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || 'Failed to update protocol';
      });

    // Admin: Delete protocol
    builder
      .addCase(deleteProtocolAdmin.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteProtocolAdmin.fulfilled, (state, action) => {
        state.deleting = false;
        state.allProtocols = state.allProtocols.filter(p => p.id !== action.payload);
        state.totalAllProtocols -= 1;
        if (state.currentAdminProtocol?.id === action.payload) {
          state.currentAdminProtocol = null;
        }
      })
      .addCase(deleteProtocolAdmin.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.error.message || 'Failed to delete protocol';
      });
  },
});

export const {
  clearError,
  clearCurrentProtocol,
  setFilters,
  setCurrentPage,
  setItemsPerPage,
  resetFilters,
} = protocolSlice.actions;

export default protocolSlice.reducer;

